import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningIcon from '@mui/icons-material/Warning';
import ChatIcon from '@mui/icons-material/Chat';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import SortIcon from '@mui/icons-material/Sort';

import { IDesignSession } from '../../../../Models/IDesignSession';
import { getFormattedDate } from '../../../../Utils/UnitsHelper';
import { Constants } from '../../../../Models/Constants';
import { ColumnConfig, BlindTestListState } from './BlindTestListTypes';
import { styles, getDynamicStyles, getTableCellStyles } from './BlindTestListStyles';

interface BlindTestListComponentsProps {
  state: BlindTestListState;
  positionSummary?: any;
  onNavigateBack: () => void;
  onNavigateToDetail: (item: IDesignSession) => void;
  onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFilterChange: (columnId: string, value: string) => void;
  onOpenColumnMenu: (event: React.MouseEvent<HTMLButtonElement>, columnId: string) => void;
  onCloseColumnMenu: () => void;
  onSortMenuClick: (direction: 'asc' | 'desc' | null) => void;
  onToggleColumnVisibility: (columnId: string) => void;
  onOpenColumnManager: () => void;
  onCloseColumnManager: () => void;
  getSortedAndFilteredData: () => any[];
  isColumnVisible: (columnId: string) => boolean;
  getVisibleColumns: () => ColumnConfig[];
  blindTests?: any[];
}

/**
 * Header component for BlindTestList
 */
export const BlindTestListHeader: React.FC<{
  positionSummary?: any;
  onNavigateBack: () => void;
  onToggleFilters: () => void;
  showFilters: boolean;
  isLoading: boolean;
}> = ({ positionSummary, onNavigateBack, onToggleFilters, showFilters, isLoading }) => {
  
  if (!positionSummary) return null;
  
  return (
    <>
      <Box sx={styles.headerContainer}>
        <Button 
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          sx={styles.backButton}
          onClick={onNavigateBack}
        >
          Back
        </Button>
        
        <Box sx={styles.headerContent}>
          <Typography variant="h5" fontWeight="bold">
            {positionSummary.alfaLavalPosition}
          </Typography>
          <Typography variant="subtitle1" sx={styles.productLine}>
            {positionSummary.productLine}
          </Typography>
          {positionSummary.blindTestDataSetId && (
            <Typography variant="subtitle1" color="text.secondary" sx={styles.productLine}>
              Dataset ID: {positionSummary.blindTestDataSetId}
            </Typography>
          )}
        </Box>
      </Box>
      
      <Box sx={styles.filtersContainer}>
        <Button
          variant="contained"
          startIcon={<FilterAltIcon />}
          disabled={isLoading}
          onClick={onToggleFilters}
        >
          {showFilters ? 'Hide filters' : 'Show filters'}
        </Button>
      </Box>
    </>
  );
};

/**
 * Filter component for BlindTestList
 */
export const BlindTestListFilter: React.FC<{
  showFilters: boolean;
  columns: ColumnConfig[];
  filterColumn: string;
  filterValue: string;
  currentFilter: string;
  onFilterChange: (columnId: string, value: string) => void;
  onSetFilterColumn: (column: string) => void;
}> = ({ showFilters, columns, filterColumn, filterValue, currentFilter, onFilterChange, onSetFilterColumn }) => {
  
  if (!showFilters) return null;
  
  return (
    <Box sx={styles.filterContainer}>
      <Typography variant="body2" sx={styles.filterLabel}>Filter by column:</Typography>
      <Box sx={styles.filterForm}>
        <Box sx={styles.filterControls}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="filter-column-label">Select column</InputLabel>
            <Select
              labelId="filter-column-label"
              value={filterColumn}
              onChange={(e) => onSetFilterColumn(e.target.value)}
              label="Select column"
            >
              <MenuItem value="">
                <em>Select column</em>
              </MenuItem>
              {columns.map(col => (
                <MenuItem key={col.id} value={col.id}>{col.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField 
            size="small"
            placeholder="Filter value" 
            value={filterValue}
            onChange={(e) => onFilterChange(filterColumn, e.target.value)}
            disabled={!filterColumn}
            sx={{ minWidth: 200 }}
          />
          
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={() => onFilterChange('', '')}
            size="small"
          >
            Clear
          </Button>
        </Box>
        
        {currentFilter && (
          <Chip
            icon={<WarningIcon />}
            label="Filters are active"
            color="warning"
            variant="outlined"
            sx={styles.warningBadge}
            onDelete={() => onFilterChange('', '')}
          />
        )}
      </Box>
    </Box>
  );
};

/**
 * Loading component for BlindTestList
 */
export const BlindTestListLoading: React.FC = () => (
  <Box sx={styles.loadingContainer}>
    <CircularProgress size={60} />
    <Typography variant="body1" sx={{ ml: 2 }}>Loading...</Typography>
  </Box>
);

/**
 * Empty state component for BlindTestList
 */
export const BlindTestListEmpty: React.FC = () => (
  <Box sx={{ textAlign: 'center', mt: 4 }}>
    <Typography variant="body1">No data available.</Typography>
  </Box>
);

/**
 * Source chip component for rendering source badges
 */
export const SourceChip: React.FC<{
  item: IDesignSession;
  onItemClick: (item: IDesignSession) => void;
}> = ({ item, onItemClick }) => {
  const theme = useTheme();
  const dynamicStyles = getDynamicStyles(theme);
  
  if (item.source?.includes('|')) {
    return (
      <Tooltip title={item.source} arrow>
        <Chip
          label="Done"
          color="success"
          onClick={() => onItemClick(item)}
          sx={dynamicStyles.sourceChipSuccess}
        />
      </Tooltip>
    );
  } 
  
  if (item.source === Constants.SOURCE_SME) {
    return (
      <Tooltip title="SME" arrow>
        <Chip
          label="SME"
          color="warning"
          onClick={() => onItemClick(item)}
          sx={styles.sourceChip}
        />
      </Tooltip>
    );
  }

  if (item.source === Constants.SOURCE_EMELIE_BETTER_THAN_SME) {
    return (
      <Tooltip title="EMELIE - better than SME" arrow>
        <Chip
          label="EMELIE"
          color="success"
          onClick={() => onItemClick(item)}
          sx={styles.sourceChip}
        />
      </Tooltip>
    );
  }
  
  if (item.source === Constants.SOURCE_EMELIE_ACCEPTABLE) {
    return (
      <Tooltip title="EMELIE - acceptable" arrow>
        <Chip
          label="EMELIE"
          color="primary"
          onClick={() => onItemClick(item)}
          sx={styles.sourceChip}
        />
      </Tooltip>
    );
  }
  
  return (
    <Button 
      variant="text" 
      size="small"
      onClick={() => onItemClick(item)}
    >
      Check
    </Button>
  );
};

/**
 * Date cell component for rendering formatted dates
 */
export const DateCell: React.FC<{
  createdDate: string;
}> = ({ createdDate }) => {
  const formattedDate = getFormattedDate(createdDate);
  return (
    <Tooltip title={formattedDate} arrow>
      <Typography variant="body2">{formattedDate}</Typography>
    </Tooltip>
  );
};

/**
 * Table cell component for rendering table cells with proper styling
 */
export const BlindTestTableCell: React.FC<{
  column: ColumnConfig;
  row: any;
  isHeader?: boolean;
  onOpenColumnMenu?: (event: React.MouseEvent<HTMLButtonElement>, columnId: string) => void;
}> = ({ column, row, isHeader = false, onOpenColumnMenu }) => {
  const { width } = column;
  const cellSx = getTableCellStyles(isHeader);
  
  return (
    <TableCell 
      key={column.id}
      align={column.align}
      sx={{
        ...cellSx,
        width: `${width.minWidth}px`,
        '&.MuiTableCell-sizeMedium': {
          ...cellSx['&.MuiTableCell-sizeMedium'],
          width: `${width.minWidth}px`,
          maxWidth: `${width.maxWidth}px`
        }
      }}
    >
      {isHeader ? (
        <Box sx={styles.columnHeaderContent}>
          <Box sx={styles.headerLabelContainer}>
            <Tooltip title={column.label} arrow>
              <Typography variant="subtitle2" fontWeight="bold" noWrap>
                {column.label}
              </Typography>
            </Tooltip>
          </Box>
          {onOpenColumnMenu && (
            <IconButton
              size="small"
              sx={styles.columnMenuButton}
              onClick={(e) => onOpenColumnMenu(e, column.id)}
              aria-label={`Menu for ${column.label}`}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ) : (
        column.format(row[column.id], row)
      )}
    </TableCell>
  );
};

/**
 * Column menu component for table column operations
 */
export const ColumnMenu: React.FC<{
  anchorEl: HTMLElement | null;
  activeColumn: string;
  onClose: () => void;
  onSortMenuClick: (direction: 'asc' | 'desc' | null) => void;
  onToggleColumnVisibility: (columnId: string) => void;
  onOpenColumnManager: () => void;
}> = ({ anchorEl, activeColumn, onClose, onSortMenuClick, onToggleColumnVisibility, onOpenColumnManager }) => (
  <Menu
    anchorEl={anchorEl}
    open={Boolean(anchorEl)}
    onClose={onClose}
  >
    <MenuItem onClick={() => onSortMenuClick('asc')}>
      <ListItemIcon>
        <ArrowUpwardIcon fontSize="small" />
      </ListItemIcon>
      Sort by ASC
    </MenuItem>
    <MenuItem onClick={() => onSortMenuClick('desc')}>
      <ListItemIcon>
        <ArrowDownwardIcon fontSize="small" />
      </ListItemIcon>
      Sort by DESC
    </MenuItem>
    <MenuItem onClick={() => onSortMenuClick(null)}>
      <ListItemIcon>
        <SortIcon fontSize="small" />
      </ListItemIcon>
      Unsort
    </MenuItem>
    <Divider />
    <MenuItem onClick={() => onToggleColumnVisibility(activeColumn)}>
      <ListItemIcon>
        <VisibilityOffIcon fontSize="small" />
      </ListItemIcon>
      Hide column
    </MenuItem>
    <MenuItem onClick={onOpenColumnManager}>
      <ListItemIcon>
        <ViewColumnIcon fontSize="small" />
      </ListItemIcon>
      Manage columns
    </MenuItem>
  </Menu>
);

/**
 * Column manager dialog component
 */
export const ColumnManagerDialog: React.FC<{
  open: boolean;
  columns: ColumnConfig[];
  onClose: () => void;
  onToggleColumnVisibility: (columnId: string) => void;
  isColumnVisible: (columnId: string) => boolean;
}> = ({ open, columns, onClose, onToggleColumnVisibility, isColumnVisible }) => (
  <Dialog
    open={open}
    onClose={onClose}
    aria-labelledby="column-manager-dialog-title"
  >
    <DialogTitle id="column-manager-dialog-title">
      Manage Columns
    </DialogTitle>
    <DialogContent dividers sx={styles.columnManagerDialog}>
      <List>
        {columns.map(column => (
          <ListItem key={column.id} dense disablePadding>
            <ListItemButton onClick={() => onToggleColumnVisibility(column.id)}>
              <Checkbox
                edge="start"
                checked={isColumnVisible(column.id)}
                tabIndex={-1}
                disableRipple
              />
              <ListItemText primary={column.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

/**
 * Main table component for BlindTestList
 */
export const BlindTestTable: React.FC<{
  columns: ColumnConfig[];
  data: any[];
  visibleColumns: ColumnConfig[];
  onItemClick: (item: any) => void;
  onOpenColumnMenu: (event: React.MouseEvent<HTMLButtonElement>, columnId: string) => void;
}> = ({ columns, data, visibleColumns, onItemClick, onOpenColumnMenu }) => (
  <TableContainer component={Paper} elevation={3} sx={styles.tableContainer}>
    <Table stickyHeader aria-label="blind test data table" sx={styles.stickyTable}>
      <TableHead>
        <TableRow>
          {visibleColumns.map(column => (
            <BlindTestTableCell
              key={column.id}
              column={column}
              row={{}}
              isHeader={true}
              onOpenColumnMenu={onOpenColumnMenu}
            />
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {data.map(row => (
          <TableRow 
            hover 
            key={row.id}
            onClick={() => onItemClick(row)}
            sx={{ '&:hover': { cursor: 'pointer' } }}
          >
            {visibleColumns.map(column => (
              <BlindTestTableCell
                key={column.id}
                column={column}
                row={row}
                isHeader={false}
              />
            ))}
          </TableRow>
        ))}
        {data.length === 0 && (
          <TableRow>
            <TableCell colSpan={visibleColumns.length} align="center">
              <Typography variant="body1" padding={3}>
                No matching records found
              </Typography>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </TableContainer>
); 